import React from 'react';
import cn from '@/utils/cn';
import { Option } from '@/components/common/ReactSelect5';
import Tooltip from '@/components/common/Tooltip';
import Icon from '@/components/ui/Icon';

type PreviousOrNext = 'previous' | 'next';

interface NavButtonProps {
  isDisabled: boolean;
  onNavClick: (value: PreviousOrNext) => void;
  tooltipContent: string;
  type: PreviousOrNext;
}

const NavButton = ({ isDisabled, onNavClick, tooltipContent, type }: NavButtonProps) => {
  const navButtonClasses = {
    'tw-min-w-[20px]': true,
    'tw-text-gray-400': isDisabled,
    'tw-text-tci-purple tw-cursor-pointer': !isDisabled,
  };

  const previousIcon = () => (
    <Icon
      className={cn(navButtonClasses)}
      icon="ArrowPrevious"
      onClick={() => onNavClick('previous')}
    />
  );

  const nextIcon = () => (
    <Icon
      className={cn(navButtonClasses)}
      icon="ArrowNext"
      onClick={() => onNavClick('next')}
    />
  );

  if (type === 'previous' && isDisabled) return previousIcon();
  if (type === 'next' && isDisabled) return nextIcon();

  return (
    <Tooltip
      content={tooltipContent}
      size="regular"
      theme="white"
    >
      {type === 'previous' ? previousIcon() : nextIcon()}
    </Tooltip>
  );
};

interface ArrowNavigatorProps {
  children: React.ReactNode;
  containerClass?: string;
  currentValue: number | string,
  isDisabled: boolean;
  label: string;
  labelClass?: string;
  navigableOptions: Option[],
  onChange: (value: string) => void,
  toggleId: string;
}

const ArrowNavigator = (
  {
    children,
    containerClass,
    currentValue,
    isDisabled,
    label,
    labelClass,
    navigableOptions,
    onChange,
    toggleId,
  }: ArrowNavigatorProps,
) => {
  const currentIndex = navigableOptions.findIndex(s => s.value.toString() === currentValue.toString());

  const previousItemIndex = currentIndex === 0 ? null : currentIndex - 1;
  const previousItem = previousItemIndex != null ? navigableOptions[previousItemIndex] : null;

  const nextItemIndex = currentIndex === navigableOptions.length - 1 ? null : currentIndex + 1;
  const nextItem = nextItemIndex != null ? navigableOptions[nextItemIndex] : null;

  const disabledForType = (type: PreviousOrNext) => (
    isDisabled || (type === 'previous' && !previousItem) || (type === 'next' && !nextItem)
  );

  const onNavClick = (type: PreviousOrNext) => {
    if (disabledForType(type)) return;

    if (type === 'previous') {
      onChange(previousItem.value);
    } else {
      onChange(nextItem.value);
    }
  };

  return (
    <label
      aria-label={label}
      className={`${containerClass} tw-w-full`}
      htmlFor={toggleId}
    >
      <div className="tw-flex tw-items-center">
        <div className="tw-min-w-[20px]" /> {/* For spacing */}
        <span className={`${labelClass} tw-block tw-mb-1 tw-ml-1`}>
          {label}
        </span>
      </div>

      <div className="tw-flex tw-items-center tw-justify-between">
        <NavButton
          isDisabled={disabledForType('previous')}
          tooltipContent={previousItem?.label}
          type="previous"
          onNavClick={onNavClick}
        />

        <div className="tw-w-full tw-px-1">
          {children}
        </div>

        <NavButton
          isDisabled={disabledForType('next')}
          tooltipContent={nextItem?.label}
          type="next"
          onNavClick={onNavClick}
        />
      </div>
    </label>
  );
};

export default ArrowNavigator;
